import React, { PureComponent } from 'react';
import moment from 'moment';
import styled from 'styled-components';

import { getRequest } from '../../api/ApiService';

// Full-page "round hasn't started" state, rendered in place of the
// normal decision page content for a student when the game has been
// reset but the instructor hasn't set up rounds yet (rounds array
// empty on the backend). Clicking "View round deadlines" fetches
// game/rounds/ live so the message reflects the current state of the
// backend rather than the stale FE snapshot.

const FONT = "'Mukta Vaani', Lato, sans-serif";

const Styled = {};

// Fills the available viewport (minus the fixed 59px topbar) and
// centers the card both horizontally and vertically. The parent
// (.app-wrapper) is a flex row, so we need flex:1 to actually span
// the remaining width next to the fixed sidebar — otherwise the
// section collapses to its content width and the card lands left-
// of-center. Negative margins cancel any parent padding.
Styled.Root = styled.section`
  flex: 1 1 auto;
  min-width: 0;
  background: #FAFBFD;
  min-height: calc(100vh - 59px);
  margin: 0 -40px -40px;
  padding: 32px;
  font-family: ${FONT};
  color: #0F172A;
  display: flex;
  align-items: center;
  justify-content: center;
`;

Styled.Card = styled.div`
  width: 100%;
  max-width: 640px;
  margin: 0 auto;
  padding: 48px 40px;
  background: #FFFFFF;
  border: 1px solid #E2E8F0;
  border-radius: 20px;
  text-align: center;
`;

Styled.Title = styled.h1`
  margin: 0 0 12px;
  font-family: ${FONT};
  font-size: 28px;
  font-weight: 800;
  color: #0F172A;
`;

Styled.Message = styled.p`
  margin: 0 auto 24px;
  max-width: 480px;
  font-size: 18px;
  line-height: 1.55;
  color: #475569;
`;

Styled.LinkButton = styled.button`
  background: transparent;
  border: 0;
  padding: 0;
  margin: 0;
  font-family: ${FONT};
  font-size: 18px;
  font-weight: 700;
  color: #2563EB;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }

  &:focus-visible {
    outline: 2px solid #2563EB;
    outline-offset: 2px;
    border-radius: 4px;
  }
`;

Styled.DeadlinesTitle = styled.h2`
  margin: 32px 0 12px;
  font-size: 20px;
  font-weight: 800;
  color: #0F172A;
`;

Styled.DeadlinesTable = styled.table`
  margin: 0 auto;
  border-collapse: collapse;
  font-size: 16px;

  th, td {
    padding: 8px 16px;
    text-align: left;
    border-bottom: 1px solid #E2E8F0;
  }

  th {
    font-weight: 700;
    color: #475569;
  }

  td {
    color: #0F172A;
  }
`;

Styled.EmptyState = styled.p`
  margin: 24px 0 0;
  font-size: 16px;
  line-height: 1.55;
  color: #475569;
`;

Styled.LoadingState = styled.p`
  margin: 24px 0 0;
  font-size: 16px;
  color: #64748B;
`;

const formatDate = (raw) => {
  if (!raw) return '—';
  const m = moment(raw);
  return m.isValid() ? m.format('MMM D, YYYY, h:mm A') : '—';
};

const roundLabel = (round) => {
  if (!round) return '';
  if (round.practice === 'Yes' || round.practice === true) return 'Practice';
  if (round.number != null) return `Round ${round.number}`;
  return '—';
};

class RoundNotStartedGate extends PureComponent {
  state = {
    // 'idle' | 'loading' | 'loaded' | 'error'
    status: 'idle',
    rounds: [],
  };

  fetchDeadlines = () => {
    if (this.state.status === 'loading') return;
    this.setState({ status: 'loading' });
    getRequest(`${process.env.REACT_APP_API_URL}game/rounds/`)
      .then(res => {
        const body = (res && res.body) || {};
        const rounds = Array.isArray(body) ? body : (Array.isArray(body.results) ? body.results : []);
        this.setState({ status: 'loaded', rounds });
      })
      .catch(() => {
        this.setState({ status: 'error' });
      });
  };

  renderDeadlinesResult = () => {
    const { status, rounds } = this.state;
    if (status === 'idle') return null;
    if (status === 'loading') {
      return <Styled.LoadingState>Loading round deadlines…</Styled.LoadingState>;
    }
    if (status === 'error') {
      return <Styled.EmptyState>Couldn't load round deadlines. Please try again.</Styled.EmptyState>;
    }
    if (status === 'loaded' && rounds.length === 0) {
      return (
        <Styled.EmptyState>
          Your instructor hasn't set up rounds yet.
        </Styled.EmptyState>
      );
    }
    return (
      <div>
        <Styled.DeadlinesTitle>Round Deadlines</Styled.DeadlinesTitle>
        <Styled.DeadlinesTable>
          <thead>
            <tr>
              <th>Round</th>
              <th>Start</th>
              <th>End</th>
            </tr>
          </thead>
          <tbody>
            {rounds.map((r, i) => (
              <tr key={r.id || i}>
                <td>{roundLabel(r)}</td>
                <td>{formatDate(r.start_date)}</td>
                <td>{formatDate(r.end_date)}</td>
              </tr>
            ))}
          </tbody>
        </Styled.DeadlinesTable>
      </div>
    );
  };

  render() {
    return (
      <Styled.Root>
        <Styled.Card>
          <Styled.Title>This round hasn't started yet</Styled.Title>
          <Styled.Message>
            Once your instructor kicks off the round, you'll be able to make
            and post decisions here.
          </Styled.Message>
          <Styled.LinkButton type="button" onClick={this.fetchDeadlines}>
            View round deadlines
          </Styled.LinkButton>
          {this.renderDeadlinesResult()}
        </Styled.Card>
      </Styled.Root>
    );
  }
}

export default RoundNotStartedGate;



// WEBPACK FOOTER //
// ./src/components/round-not-started/index.js